import { useMemo } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common';
import submissionHeadersQuery from 'bundles/grading/api/SubmissionHeadersQuery.graphql';
import submissionHeadersQueryV2 from 'bundles/grading/api/SubmissionHeadersQueryV2.graphql';
import type {
  SubmissionHeadersQuery,
  SubmissionHeadersQueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionHeadersQuery';
import type {
  SubmissionHeadersV2Query,
  SubmissionHeadersV2QueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionHeadersQueryV2';
import type { SubmissionHeader } from 'bundles/grading/api/types';
import { getScopedSubmitterIdFromRouteSubmitterId } from 'bundles/grading/utils/submitterUtils';

export type UseSubmissionHeadersResult = {
  loading: boolean;
  data: SubmissionHeader[];
  error?: ApolloError;
};

/**
 * This hook sends a query to fetch a list of submission attempts
 */
const useSubmissionHeadersQuery = (
  variables: SubmissionHeadersQueryVariables,
  courseId: string,
  routeSubmitterId: string
): UseSubmissionHeadersResult => {
  const useApiV2 = isAssignmentGradingInPublicEnabled();

  const result = useQuery<SubmissionHeadersQuery, SubmissionHeadersQueryVariables>(submissionHeadersQuery, {
    variables,
    context: { clientName: 'gatewayGql' },
    skip: useApiV2,
    onError: (error) => {
      Sentry.captureException('Error fetching SubmissionHeaders in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const headers = useMemo(() => {
    // Reset previously loaded headers when submitterId changes in the URL.
    // This way clicking "Submission history" button won't query AttemptData for
    // curr submitterId and attemptId from headers loaded for prev submitterId
    if (result.loading) {
      return [];
    }
    const submissionHeaders = [
      ...(result.data?.GroupGrading?.queryGroup?.items?.[0]?.gradingTasks?.[0]?.submissionHeaders || []),
    ] as SubmissionHeader[];

    // sort by attempt number - attempts with higher number first.
    submissionHeaders.sort((header1, header2) => header2.attemptNumber - header1.attemptNumber);

    return submissionHeaders;
  }, [result.data, result.loading]);

  const variablesV2 = {
    itemId: variables.itemFilter?.itemIds?.[0] || '',
    courseId,
    scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId),
  };

  const resultV2 = useQuery<SubmissionHeadersV2Query, SubmissionHeadersV2QueryVariables>(submissionHeadersQueryV2, {
    variables: variablesV2,
    context: { clientName: 'gatewayGql' },
    skip: !useApiV2,
    onError: (error) => {
      Sentry.captureException('Error fetching SubmissionHeaders V2 in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const headersV2 = useMemo(() => {
    // Reset previously loaded headers when submitterId changes in the URL.
    // This way clicking "Submission history" button won't query AttemptData for
    // curr submitterId and attemptId from headers loaded for prev submitterId
    if (resultV2.loading) {
      return [];
    }
    const submissionHeaders = [...(resultV2.data?.GroupGrading?.querySubmissionHeaders || [])] as SubmissionHeader[];

    // sort by attempt number - attempts with higher number first.
    submissionHeaders.sort((header1, header2) => header2.attemptNumber - header1.attemptNumber);

    return submissionHeaders;
  }, [resultV2.data, resultV2.loading]);

  if (useApiV2) {
    return {
      loading: resultV2.loading,
      data: headersV2,
      error: resultV2.error,
    };
  }

  return {
    loading: result.loading,
    data: headers,
    error: result.error,
  };
};

export default useSubmissionHeadersQuery;
